import PropTypes from "prop-types";
import { useMemo } from "react";
// @mui
import { CssBaseline, responsiveFontSizes } from "@mui/material";
import {
  ThemeProvider as MUIThemeProvider,
  createTheme,
  StyledEngineProvider,
} from "@mui/material/styles";
//
import palette from "./palette";
import shadows from "./shadows";
import typography from "./typography";
import GlobalStyles from "./globalStyles";
import customShadows from "./customShadows";

// ----------------------------------------------------------------------

const ThemeProvider = (props) => {
  const { children } = props;

  const themeOptions = useMemo(
    () => ({
      palette,
      shape: { borderRadius: 6 },
      typography,
      shadows: shadows(),
      customShadows: customShadows(),
    }),
    []
  );

  const theme = createTheme(themeOptions);
  theme.components = {
    MuiCssBaseline: {
      styleOverrides: `
        input:-webkit-autofill,
        input:-webkit-autofill:hover,
        input:-webkit-autofill:focus,
        textarea:-webkit-autofill,
        textarea:-webkit-autofill:hover,
        textarea:-webkit-autofill:focus,
        select:-webkit-autofill,
        select:-webkit-autofill:hover,
        select:-webkit-autofill:focus {
          -webkit-text-fill-color: #FFFFFF;
          -webkit-box-shadow: 0 0 0px 1000px transparent inset;
          transition: background-color 5000s ease-in-out 0s;
        }
      `,
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: "40px",
          fontWeight: 500,
          textTransform: "uppercase",
          color: "white",
          [theme.breakpoints.up("lg")]: {
            fontSize: "20px",
          },
          [theme.breakpoints.down("md")]: {
            fontSize: "18px",
          },
        },
        contained: {
          border: "0.5px solid white",
          boxShadow: "0px 0px 0px 4px  rgba(255, 255, 255, 0.08)",
        },
      },
    },
    MuiButtonBase: {
      styleOverrides: {
        root: {
          paddingBottom: "0px !important",
        },
        contained: {
          border: "0.5px solid white",
          boxShadow: "0px 0px 0px 4px  rgba(255, 255, 255, 0.08)",
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: "38px",
          "&:hover .MuiOutlinedInput-notchedOutline": {
            border: "1px solid #088848",
          },
        },
        notchedOutline: {
          border: "1px solid rgba(255,255,255,0.2)",
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: "50px",
          backgroundColor: "#111827",
          padding: "24px 56px 24px 56px !important",
          border: "1px solid #FFFFFF",
          boxShadow: "0 0 0 7px  #FFFFFF14, 0 0 54px 0  #0CAF6033",
        },
      },
      defaultProps: {
        slotProps: {
          backdrop: {
            sx: {
              backgroundColor: "#111827B2",
            },
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontFamily: "DM Sans",
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: 0,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: 0,
          color: "white",
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: 0,
          color: "white",
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          "&::placeholder": {
            color: "#9e9e9e !important",
            fontSize: "14px !important",
            fontFamily: "DM Sans",
            opacity: 1,
          },
        },
        root: {
          color: "white",
          fontFamily: "DM Sans",
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: "white !important",
          fontFamily: "DM Sans",
          fontSize: "14px",
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: 0,
          "&:last-child": {
            paddingBottom: 0,
          },
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          fontFamily: '"DM Sans", serif',
          [theme.breakpoints.up("lg")]: {
            fontSize: "32px",
          },
          [theme.breakpoints.down("lg")]: {
            fontSize: "26px",
          },
          [theme.breakpoints.down("md")]: {
            fontSize: "20px",
          },
          fontWeight: 700,
          color: "white",
        },
        secondary: {
          fontFamily: '"DM Sans", serif',
          fontSize: "15px",
          fontWeight: 400,
          color: "white",
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          width: "54px",
          height: "54px",
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: "rgba(12, 175, 96, 1)",
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          color: "white",
          borderBottom: "1px solid rgba(46, 58, 84, 1)",
          fontFamily: '"DM Sans", serif',
          fontSize: "12px",
          fontWeight: 300,
        },
        head: {
          color: "white",
          fontFamily: '"DM Sans", serif',
          fontSize: "12px",
        },
      },
    },
  };

  return (
    <StyledEngineProvider injectFirst>
      <MUIThemeProvider theme={responsiveFontSizes(theme)}>
        <CssBaseline />
        <GlobalStyles />
        {children}
      </MUIThemeProvider>
    </StyledEngineProvider>
  );
};

ThemeProvider.propTypes = {
  children: PropTypes.node,
};

export default ThemeProvider;
